import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft, FiSearch, FiFilter, FiCalendar, FiMapPin,
  FiAlertCircle, FiChevronDown, FiX, FiUserX, FiBookOpen
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { recitativoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './FaltasRecitativos.css';

const TURMA_CURTA = { 1: '1ª turma', 2: '2ª turma', 3: '3ª turma' };

function formatarTelefoneWA(telefone) {
  const digits = (telefone || '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('55') && digits.length >= 12) return digits;
  return '55' + digits;
}

const fmtData = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '—'; }
};

const primeiroNome = (nome) => (nome || '').trim().split(/\s+/)[0] || '';

const FaltasRecitativos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [faltas, setFaltas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [busca, setBusca] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroLocalidade, setFiltroLocalidade] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [expandido, setExpandido] = useState(() => new Set());

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const isAdmin = user?.permissao === 'admin' || user?.permissao === 'editor';
      const resp = await recitativoService.listarFaltas(isAdmin ? {} : { meus: true });
      setFaltas(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar faltas');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  const localidades = useMemo(() => {
    const set = new Set();
    faltas.forEach((f) => { if (f.recitativo.localidade) set.add(f.recitativo.localidade); });
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [faltas]);

  // Aplica filtros sobre as faltas individuais
  const faltasFiltradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const di = dataInicio ? new Date(dataInicio + 'T00:00:00') : null;
    const df = dataFim ? new Date(dataFim + 'T23:59:59') : null;
    return faltas.filter((f) => {
      if (filtroTurma && String(f.recitativo.turma) !== String(filtroTurma)) return false;
      if (filtroTipo && f.recitativo.tipo_pessoa !== filtroTipo) return false;
      if (filtroLocalidade && f.recitativo.localidade !== filtroLocalidade) return false;
      if (di || df) {
        const d = new Date(f.recitativo.data_referencia);
        if (di && d < di) return false;
        if (df && d > df) return false;
      }
      if (q && !(f.membro.nome_completo || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [faltas, busca, filtroTurma, filtroTipo, filtroLocalidade, dataInicio, dataFim]);

  // Agrupa por membro
  const grupos = useMemo(() => {
    const map = new Map();
    for (const f of faltasFiltradas) {
      const key = f.membro.id;
      if (!map.has(key)) {
        map.set(key, { membro: f.membro, ocorrencias: [] });
      }
      map.get(key).ocorrencias.push(f);
    }
    const arr = [...map.values()];
    // Ordena: mais faltas primeiro, depois nome
    arr.sort((a, b) => {
      if (b.ocorrencias.length !== a.ocorrencias.length) return b.ocorrencias.length - a.ocorrencias.length;
      return (a.membro.nome_completo || '').localeCompare(b.membro.nome_completo || '', 'pt-BR');
    });
    return arr;
  }, [faltasFiltradas]);

  const totalPessoas = grupos.length;
  const totalFaltas = faltasFiltradas.length;

  const temFiltro = busca || filtroTurma || filtroTipo || filtroLocalidade || dataInicio || dataFim;
  const limparFiltros = () => {
    setBusca(''); setFiltroTurma(''); setFiltroTipo('');
    setFiltroLocalidade(''); setDataInicio(''); setDataFim('');
  };

  const toggleExpandido = (id) => {
    setExpandido((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id); else novo.add(id);
      return novo;
    });
  };

  const linkWhatsApp = (grupo) => {
    const { membro } = grupo;
    const ehJovem = membro.idade != null && membro.idade >= 12;
    // Jovem fala direto; criança usa o responsável
    const alvoTel = ehJovem ? membro.telefone : (membro.responsavel?.telefone || membro.telefone);
    const tel = formatarTelefoneWA(alvoTel);
    if (!tel) return null;
    const nome = primeiroNome(ehJovem ? membro.nome_completo : (membro.responsavel?.nome_completo || membro.nome_completo));
    const sobre = ehJovem ? '' : ` do(a) ${primeiroNome(membro.nome_completo)}`;
    const msg = `A Paz de Deus, ${nome}! Sentimos sua falta${sobre} na reunião de jovens. Esperamos você no próximo encontro!`;
    return `https://wa.me/${tel}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="fr-page">
      <header className="fr-top">
        <button type="button" className="fr-back" onClick={() => navigate('/recitativos')} aria-label="Voltar">
          <FiArrowLeft />
        </button>
        <div className="fr-top-info">
          <h1><FiUserX /> Faltas nos Recitativos</h1>
          <p>Acompanhe quem faltou e fale com cada um.</p>
        </div>
      </header>

      {/* Resumo */}
      <div className="fr-resumo">
        <div className="fr-resumo-card">
          <strong>{totalPessoas}</strong>
          <small>pessoa(s)</small>
        </div>
        <div className="fr-resumo-card fr-resumo-faltas">
          <strong>{totalFaltas}</strong>
          <small>falta(s)</small>
        </div>
      </div>

      {/* Busca + botão de filtros */}
      <div className="fr-busca-row">
        <div className="fr-search">
          <FiSearch />
          <input
            type="text"
            placeholder="Buscar por nome…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {busca && (
            <button type="button" className="fr-search-clear" onClick={() => setBusca('')} aria-label="Limpar busca">
              <FiX />
            </button>
          )}
        </div>
        <button
          type="button"
          className={`fr-btn-filtros${filtrosAbertos ? ' aberto' : ''}${temFiltro && !busca ? ' tem-filtro' : ''}`}
          onClick={() => setFiltrosAbertos((v) => !v)}
        >
          <FiFilter />
          <span>Filtros</span>
          <FiChevronDown className="fr-chevron" />
        </button>
      </div>

      {/* Painel de filtros */}
      {filtrosAbertos && (
        <div className="fr-filtros-painel">
          <label className="fr-filtro">
            <span>Turma</span>
            <select value={filtroTurma} onChange={(e) => setFiltroTurma(e.target.value)}>
              <option value="">Todas</option>
              <option value="1">1ª turma (3-6)</option>
              <option value="2">2ª turma (7-11)</option>
              <option value="3">3ª turma (12+)</option>
            </select>
          </label>
          <label className="fr-filtro">
            <span>Tipo</span>
            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
              <option value="">Todos</option>
              <option value="irmao">Irmãos</option>
              <option value="irma">Irmãs</option>
            </select>
          </label>
          <label className="fr-filtro">
            <span>Localidade</span>
            <select value={filtroLocalidade} onChange={(e) => setFiltroLocalidade(e.target.value)}>
              <option value="">Todas</option>
              {localidades.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <div className="fr-filtro-datas">
            <label className="fr-filtro">
              <span>De</span>
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </label>
            <label className="fr-filtro">
              <span>Até</span>
              <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </label>
          </div>
          {temFiltro && (
            <button type="button" className="fr-limpar" onClick={limparFiltros}>
              <FiX /> Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="fr-loading">Carregando faltas…</div>
      ) : grupos.length === 0 ? (
        <div className="fr-vazio">
          <FiAlertCircle size={36} />
          <h3>Nenhuma falta encontrada</h3>
          <p>{faltas.length === 0
            ? 'Ainda não há faltas registradas nos recitativos.'
            : 'Ajuste os filtros para ver outros resultados.'}</p>
        </div>
      ) : (
        <ul className="fr-lista">
          {grupos.map((g) => {
            const { membro, ocorrencias } = g;
            const ehJovem = membro.idade != null && membro.idade >= 12;
            const wa = linkWhatsApp(g);
            const aberto = expandido.has(membro.id);
            const ultima = ocorrencias[0]?.recitativo?.data_referencia;
            return (
              <li key={membro.id} className="fr-card">
                <div className="fr-card-head">
                  <div className="fr-card-pessoa">
                    <span className="fr-card-nome">{membro.nome_completo}</span>
                    <div className="fr-card-tags">
                      {membro.idade != null && (
                        <span className="fr-tag-turma">
                          {membro.idade >= 12 ? '12+' : `${membro.idade} anos`}
                        </span>
                      )}
                      <span className={`fr-tag-tipo tipo-${membro.tipo_pessoa}`}>
                        {membro.tipo_pessoa === 'irmao' ? 'Irmão' : 'Irmã'}
                      </span>
                    </div>
                    {!ehJovem && membro.responsavel && (
                      <span className="fr-card-resp">resp.: {membro.responsavel.nome_completo}</span>
                    )}
                  </div>
                  <div className="fr-card-contagem">
                    <strong>{ocorrencias.length}</strong>
                    <small>{ocorrencias.length === 1 ? 'falta' : 'faltas'}</small>
                  </div>
                </div>

                <div className="fr-card-ultima">
                  <FiCalendar size={13} /> Última: {fmtData(ultima)}
                </div>

                <div className="fr-card-acoes">
                  {wa ? (
                    <a className="fr-btn-wa" href={wa} target="_blank" rel="noopener noreferrer">
                      <FaWhatsapp /> {ehJovem ? 'Falar' : 'Falar c/ responsável'}
                    </a>
                  ) : (
                    <span className="fr-sem-tel">Sem telefone</span>
                  )}
                  <button
                    type="button"
                    className={`fr-btn-detalhes${aberto ? ' aberto' : ''}`}
                    onClick={() => toggleExpandido(membro.id)}
                  >
                    {aberto ? 'Ocultar' : 'Ver faltas'} <FiChevronDown className="fr-chevron" />
                  </button>
                </div>

                {aberto && (
                  <ul className="fr-ocorrencias">
                    {ocorrencias.map((o) => (
                      <li key={o.id} className="fr-ocorrencia">
                        <Link to={`/recitativos/${o.recitativo.id}`} className="fr-ocorrencia-link">
                          <span className="fr-oc-data"><FiCalendar size={12} /> {fmtData(o.recitativo.data_referencia)}</span>
                          <span className="fr-oc-local"><FiMapPin size={12} /> {o.recitativo.localidade}</span>
                          <span className="fr-oc-turma">{TURMA_CURTA[o.recitativo.turma]}</span>
                          <span className="fr-oc-ref"><FiBookOpen size={12} /> {o.recitativo.livro} {o.capitulo}:{o.verso}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FaltasRecitativos;
